<script setup lang="ts">
import HasPermission from '@/components/HasPermission.vue'
import useHasPermissionUser from '@/composables/useHasPermissionUser'
import CreateRoleModal from '@/modules/roles/components/CreateRoleModal.vue'
import { provide, ref, watch } from 'vue'
import useInternalRoles from '../composables/useInternalRoles'
import RoleEditPanel from '../components/RoleEditPanel.vue'

const { abilities, redirectTo404, hasPermissionUser } = useHasPermissionUser()

watch(abilities, () => { redirectTo404('view-roles', 'App\\Models\\Company') }, { immediate: true })

const { roles, isLoading, refetch, deleteRoleAsync } = useInternalRoles({ immediate: true })

const createModalOpen = ref(false)
const deleteConfirmId = ref<number | null>(null)
const editPanelRoleId = ref<number | null>(null)

async function handleDelete(id: number) {
  deleteConfirmId.value = null
  await deleteRoleAsync(id)
  await refetch()
}

const invalidateQueryData = async () => { await refetch() }
provide('invalidateQueryData', { invalidateQueryData })
</script>

<template>
  <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Roles internos</h1>
        <p class="text-sm text-gray-500 mt-0.5">Roles del sistema Enlacebooking</p>
      </div>
      <HasPermission name-permission="create-internal" entity-type="roles">
        <button
          type="button"
          class="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#021F58] text-white text-sm font-medium hover:bg-[#021F58]/90 transition-colors shadow-sm"
          @click="createModalOpen = true"
        >
          <span class="material-symbols-outlined text-[18px]">add</span>
          Nuevo rol
        </button>
      </HasPermission>
    </div>

    <!-- Loading skeleton -->
    <div v-if="isLoading" class="space-y-2 animate-pulse">
      <div v-for="i in 4" :key="i" class="h-14 rounded-xl bg-gray-100" />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!roles || roles.length === 0"
      class="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200 text-gray-400"
    >
      <span class="material-symbols-outlined text-5xl text-gray-200 mb-3">policy</span>
      <p class="text-sm font-medium text-gray-500">Sin roles internos registrados</p>
    </div>

    <!-- Roles table -->
    <div v-else class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-gray-100 bg-gray-50">
            <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Nombre</th>
            <th class="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Miembros</th>
            <th class="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Acciones</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-50">
          <tr
            v-for="role in roles"
            :key="role.id"
            class="hover:bg-gray-50/60 transition-colors"
          >
            <td class="px-5 py-3.5">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 rounded-lg bg-[#021F58]/8 flex items-center justify-center flex-shrink-0">
                  <span class="material-symbols-outlined text-[16px] text-[#021F58]">shield_person</span>
                </div>
                <div>
                  <p class="font-medium text-gray-900">{{ role.name }}</p>
                  <p v-if="role.title" class="text-xs text-gray-400">{{ role.title }}</p>
                </div>
              </div>
            </td>

            <td class="px-5 py-3.5 text-center">
              <span class="inline-flex items-center gap-1 text-sm text-gray-600">
                <span class="material-symbols-outlined text-[16px] text-gray-400">group</span>
                {{ role.usersCount }}
              </span>
            </td>

            <td class="px-5 py-3.5">
              <div class="flex items-center justify-end gap-1.5">
                <button
                  v-if="hasPermissionUser('viewAny-internal', 'roles')"
                  type="button"
                  class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:border-[#021F58] hover:text-[#021F58] transition-colors"
                  @click="editPanelRoleId = role.id"
                >
                  <span class="material-symbols-outlined text-[15px]">edit</span>
                  Editar
                </button>

                <template v-if="role.name !== 'Master' && hasPermissionUser('delete-internal', 'roles')">
                  <template v-if="deleteConfirmId === role.id">
                    <span class="text-xs text-gray-500 mr-1">¿Eliminar?</span>
                    <button
                      type="button"
                      class="inline-flex items-center px-2.5 py-1.5 rounded-lg text-xs font-medium bg-red-500 text-white border border-red-500 hover:bg-red-600 transition-colors"
                      @click="handleDelete(role.id)"
                    >
                      Sí
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center px-2.5 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                      @click="deleteConfirmId = null"
                    >
                      No
                    </button>
                  </template>
                  <button
                    v-else
                    type="button"
                    class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600 transition-colors"
                    @click="deleteConfirmId = role.id"
                  >
                    <span class="material-symbols-outlined text-[15px]">delete</span>
                  </button>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="px-5 py-2.5 border-t border-gray-100 bg-gray-50">
        <p class="text-xs text-gray-400">{{ roles.length }} rol{{ roles.length !== 1 ? 'es' : '' }} en total</p>
      </div>
    </div>

    <CreateRoleModal
      :visible="createModalOpen"
      :company-selected="undefined"
      @action-modal="createModalOpen = false"
    />

    <RoleEditPanel
      v-if="editPanelRoleId !== null"
      :key="editPanelRoleId"
      :role-id="editPanelRoleId"
      :open="editPanelRoleId !== null"
      @close="editPanelRoleId = null; refetch()"
    />

  </div>
</template>
